import { useState, useEffect, useRef } from "react";
import { Volume2, VolumeX } from "lucide-react";

// Avatar states for animation
const AVATAR_STATES = {
  IDLE: "idle",
  SPEAKING: "speaking",
  LISTENING: "listening"
};

// Voice settings
const VOICE_SETTINGS = {
  rate: 0.9,
  pitch: 1.0,
  volume: 1.0,
  preferredVoices: ["Google UK English Female", "Microsoft Hazel", "Samantha", "Karen", "en-GB"]
};

// GLOBAL speech state - prevents multiple components from speaking
let globalSpeechLock = {
  currentSubmissionId: null,
  lockedOutcome: null,
  hasFinalSpeechStarted: false,
  lastSpeechMessage: null
};

// Reset global state (exported for page transitions)
// NOTE: DO NOT call speechSynthesis.cancel() here - it kills useAutoSpeak payloads on route navigation
export function resetGlobalSpeechState() {
  console.log("[AiAvatar] Resetting global speech state (NO cancel)");
  // REMOVED: window.speechSynthesis?.cancel(); - This was killing useAutoSpeak on navigation
  globalSpeechLock = {
    currentSubmissionId: null,
    lockedOutcome: null,
    hasFinalSpeechStarted: false,
    lastSpeechMessage: null
  };
}

export function AiAvatar({ 
  message, 
  autoSpeak = true, 
  onSpeakComplete,
  size = "medium",
  showControls = true,
  className = "",
  submissionId = null,  // Unique ID for this submission attempt
  outcomeType = null    // 'success', 'error', or 'pending' - MUST match to allow speech
}) {
  const [avatarState, setAvatarState] = useState(AVATAR_STATES.IDLE);
  const [isMuted, setIsMuted] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const utteranceRef = useRef(null);
  const hasSpokeRef = useRef(false);
  const mountedRef = useRef(true);

  // Check TTS support
  useEffect(() => {
    if (!window.speechSynthesis) {
      setIsSupported(false);
      console.warn("Speech synthesis not supported in this browser");
    }
    
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Get the best available voice
  const getBestVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    
    // Try to find preferred voices
    for (const preferred of VOICE_SETTINGS.preferredVoices) {
      const found = voices.find(v => 
        v.name.includes(preferred) || v.lang.startsWith(preferred)
      );
      if (found) return found;
    }
    
    // Fallback to any English voice
    const englishVoice = voices.find(v => v.lang.startsWith("en"));
    return englishVoice || voices[0];
  };

  // Core speak function - handles the actual TTS
  const doSpeak = (text) => {
    if (!isSupported || isMuted || !text || !mountedRef.current) return;

    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;

    const voice = getBestVoice();
    if (voice) utterance.voice = voice;
    
    utterance.rate = VOICE_SETTINGS.rate;
    utterance.pitch = VOICE_SETTINGS.pitch;
    utterance.volume = VOICE_SETTINGS.volume;

    utterance.onstart = () => {
      if (mountedRef.current) {
        setAvatarState(AVATAR_STATES.SPEAKING);
      }
      console.log("[AiAvatar] Speech STARTED:", text.substring(0, 40) + "...");
    };

    utterance.onend = () => {
      if (mountedRef.current) {
        setAvatarState(AVATAR_STATES.IDLE);
        onSpeakComplete?.();
      }
      console.log("[AiAvatar] Speech ENDED");
    };

    utterance.onerror = (e) => {
      console.error("[AiAvatar] Speech error:", e);
      if (mountedRef.current) {
        setAvatarState(AVATAR_STATES.IDLE);
      }
    };

    window.speechSynthesis.speak(utterance);
  };

  // MAIN SPEECH CONTROLLER - uses global lock to prevent cross-component issues
  useEffect(() => {
    if (!autoSpeak || !message) return;
    
    // GLOBAL LOCK CHECK: If this is an outcome speech (has submissionId), enforce strict rules
    if (submissionId && outcomeType) {
      // Check if another submission already has the lock
      if (globalSpeechLock.currentSubmissionId && 
          globalSpeechLock.currentSubmissionId !== submissionId) {
        console.log(`[AiAvatar] BLOCKED: Different submission has lock (${globalSpeechLock.currentSubmissionId})`);
        return;
      }
      
      // Check if final speech already started for this submission
      if (globalSpeechLock.currentSubmissionId === submissionId && 
          globalSpeechLock.hasFinalSpeechStarted) {
        console.log(`[AiAvatar] BLOCKED: Final speech already started for ${submissionId}`);
        return;
      }
      
      // Check for outcome mismatch (e.g., trying to speak success after error was locked)
      if (globalSpeechLock.lockedOutcome && globalSpeechLock.lockedOutcome !== outcomeType) {
        console.log(`[AiAvatar] BLOCKED: Outcome mismatch (locked: ${globalSpeechLock.lockedOutcome}, trying: ${outcomeType})`);
        return;
      }
      
      // Acquire the lock
      console.log(`[AiAvatar] Acquiring lock: submission=${submissionId}, outcome=${outcomeType}`);
      globalSpeechLock.currentSubmissionId = submissionId;
      globalSpeechLock.lockedOutcome = outcomeType;
    }
    
    // Prevent duplicate speech of same message
    if (hasSpokeRef.current && globalSpeechLock.lastSpeechMessage === message) {
      console.log("[AiAvatar] BLOCKED: Same message already spoken");
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis?.cancel();
    
    // Small delay for voice loading, then speak
    const timer = setTimeout(() => {
      if (!mountedRef.current) return;
      
      // Mark as final speech if this is an outcome
      if (submissionId && outcomeType) {
        globalSpeechLock.hasFinalSpeechStarted = true;
        globalSpeechLock.lastSpeechMessage = message;
        console.log(`[AiAvatar] LOCKED final speech: ${outcomeType}`);
      }
      
      hasSpokeRef.current = true;
      doSpeak(message);
    }, 250);

    return () => clearTimeout(timer);
  }, [message, autoSpeak, submissionId, outcomeType]);

  // Cleanup on unmount - DO NOT cancel speech here, it breaks cross-screen TTS
  useEffect(() => {
    return () => {
      // REMOVED: window.speechSynthesis?.cancel(); - breaks useAutoSpeak on navigation
      console.log("[AiAvatar] Component unmounting (speech NOT cancelled)");
    };
  }, []);

  // Load voices
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  }, []);

  // Manual speak function for replay/unmute
  const speak = (text, force = false) => {
    if (force) {
      window.speechSynthesis?.cancel();
      doSpeak(text);
    }
  };

  const toggleMute = () => {
    if (isMuted) {
      setIsMuted(false);
      // Replay message if unmuting - force speak
      if (message) speak(message, true);
    } else {
      setIsMuted(true);
      window.speechSynthesis?.cancel();
      setAvatarState(AVATAR_STATES.IDLE);
    }
  };

  const replayMessage = () => {
    if (message && !isMuted) {
      speak(message, true);  // Force replay
    }
  };

  // Size classes
  const sizeClasses = {
    small: "w-16 h-16",
    medium: "w-24 h-24",
    large: "w-32 h-32"
  };

  const iconSizes = {
    small: "w-8 h-8",
    medium: "w-12 h-12",
    large: "w-16 h-16"
  };

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`} data-testid="ai-avatar">
      {/* Avatar Circle - Friendly Woman Full Body */}
      <div 
        className={`relative cursor-pointer transition-all duration-300 ${
          avatarState === AVATAR_STATES.SPEAKING ? "animate-pulse" : ""
        }`}
        onClick={replayMessage}
        title="Click to replay message"
        data-testid="avatar-circle"
        style={{
          width: size === "small" ? "80px" : size === "medium" ? "120px" : "160px",
          height: size === "small" ? "120px" : size === "medium" ? "180px" : "240px"
        }}
      >
        {/* Friendly Woman Full Body Avatar */}
        <svg 
          viewBox="0 0 100 150" 
          className="w-full h-full"
          data-testid="avatar-face"
        >
          {/* Background glow when speaking */}
          {avatarState === AVATAR_STATES.SPEAKING && (
            <ellipse cx="50" cy="75" rx="45" ry="70" fill="url(#speakingGlow)" opacity="0.3">
              <animate attributeName="opacity" values="0.3;0.5;0.3" dur="1s" repeatCount="indefinite" />
            </ellipse>
          )}
          
          <defs>
            <linearGradient id="speakingGlow" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#5EEAD4" />
              <stop offset="100%" stopColor="#818CF8" />
            </linearGradient>
            <linearGradient id="shirtGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#2563EB" />
            </linearGradient>
          </defs>
          
          {/* Body / Shirt */}
          <path d="M 25 95 Q 20 100 18 130 L 18 150 L 82 150 L 82 130 Q 80 100 75 95 Q 65 88 50 88 Q 35 88 25 95" fill="url(#shirtGradient)" />
          
          {/* Collar */}
          <path d="M 38 88 L 50 100 L 62 88" stroke="white" strokeWidth="2" fill="none" />
          
          {/* Neck */}
          <rect x="42" y="78" width="16" height="12" rx="2" fill="#F5D0C5" />
          
          {/* Hair - Back */}
          <ellipse cx="50" cy="45" rx="32" ry="35" fill="#4A3728" />
          
          {/* Face */}
          <ellipse cx="50" cy="50" rx="26" ry="28" fill="#F5D0C5" />
          
          {/* Hair - Front/Bangs */}
          <path d="M 22 42 Q 28 25 50 22 Q 72 25 78 42 Q 72 35 50 30 Q 28 35 22 42" fill="#4A3728" />
          <path d="M 20 50 Q 22 60 24 68" stroke="#4A3728" strokeWidth="6" fill="none" strokeLinecap="round" />
          <path d="M 80 50 Q 78 60 76 68" stroke="#4A3728" strokeWidth="6" fill="none" strokeLinecap="round" />
          
          {/* Eyebrows */}
          <path d="M 34 42 Q 40 39 46 42" stroke="#5D4E42" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          <path d="M 54 42 Q 60 39 66 42" stroke="#5D4E42" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          
          {/* Eyes */}
          <ellipse cx="40" cy="50" rx="5" ry="4" fill="white" />
          <ellipse cx="60" cy="50" rx="5" ry="4" fill="white" />
          
          {/* Pupils */}
          <circle cx="40" cy="51" r="2.5" fill="#3B5998">
            {avatarState === AVATAR_STATES.SPEAKING && (
              <animate attributeName="cy" values="51;50;51" dur="0.5s" repeatCount="indefinite" />
            )}
          </circle>
          <circle cx="60" cy="51" r="2.5" fill="#3B5998">
            {avatarState === AVATAR_STATES.SPEAKING && (
              <animate attributeName="cy" values="51;50;51" dur="0.5s" repeatCount="indefinite" />
            )}
          </circle>
          
          {/* Eye sparkle */}
          <circle cx="38" cy="49" r="1" fill="white" />
          <circle cx="58" cy="49" r="1" fill="white" />
          
          {/* Nose */}
          <path d="M 50 52 Q 48 56 50 58" stroke="#E8B4A8" strokeWidth="1.5" fill="none" />
          
          {/* Blush */}
          <ellipse cx="32" cy="58" rx="4" ry="2.5" fill="#FFB6C1" opacity="0.5" />
          <ellipse cx="68" cy="58" rx="4" ry="2.5" fill="#FFB6C1" opacity="0.5" />
          
          {/* Mouth */}
          {avatarState === AVATAR_STATES.SPEAKING ? (
            <>
              <ellipse cx="50" cy="66" rx="8" ry="5" fill="#E57373">
                <animate attributeName="ry" values="5;3;6;4;5" dur="0.35s" repeatCount="indefinite" />
              </ellipse>
              <ellipse cx="50" cy="64" rx="4" ry="1.5" fill="white" />
            </>
          ) : (
            <path d="M 42 64 Q 50 72 58 64" stroke="#E57373" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          )}
          
          {/* Earrings */}
          <circle cx="23" cy="52" r="2" fill="#FFD700" />
          <circle cx="77" cy="52" r="2" fill="#FFD700" />
          
          {/* Arms */}
          <path d="M 18 105 Q 10 115 12 135" stroke="url(#shirtGradient)" strokeWidth="10" fill="none" strokeLinecap="round" />
          <path d="M 82 105 Q 90 115 88 135" stroke="url(#shirtGradient)" strokeWidth="10" fill="none" strokeLinecap="round" />
          
          {/* Hands */}
          <ellipse cx="12" cy="138" rx="6" ry="5" fill="#F5D0C5" />
          <ellipse cx="88" cy="138" rx="6" ry="5" fill="#F5D0C5" />
          
          {/* Name badge */}
          <rect x="55" y="105" width="18" height="10" rx="2" fill="white" />
          <text x="64" y="112" fontSize="5" fill="#3B82F6" textAnchor="middle" fontWeight="bold">HELP</text>
        </svg>

        {/* Speaking indicator waves */}
        {avatarState === AVATAR_STATES.SPEAKING && (
          <div className="absolute -right-3 top-1/3 flex flex-col gap-1">
            <div className="w-2 h-2 bg-teal-400 rounded-full animate-ping" style={{ animationDelay: "0ms" }} />
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-ping" style={{ animationDelay: "150ms" }} />
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: "300ms" }} />
          </div>
        )}
      </div>

      {/* Controls */}
      {showControls && (
        <button
          onClick={toggleMute}
          className={`p-2 rounded-full transition-colors ${
            isMuted 
              ? "bg-red-100 text-red-600 hover:bg-red-200" 
              : "bg-blue-100 text-blue-600 hover:bg-blue-200"
          }`}
          title={isMuted ? "Unmute voice" : "Mute voice"}
          data-testid="mute-button"
        >
          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
      )}

      {/* Message display (optional) */}
      {message && (
        <div 
          className="max-w-md text-center text-slate-600 text-sm leading-relaxed bg-white/80 backdrop-blur-sm rounded-xl px-4 py-2 shadow-sm"
          data-testid="avatar-message"
        >
          {message}
        </div>
      )}
    </div>
  );
}

// Preset messages for each kiosk step
export const KIOSK_MESSAGES = {
  welcome: "Welcome to ParkWise Designated Parking Space Management.",
  category: "Please select your parking category from the options shown. Tap on your category, then tap Continue to proceed.",
  form: "Please enter your vehicle registration number and personal details. All fields marked with an asterisk are required.",
  badge_capture: "Now let's capture your Blue Badge. Please hold your badge steady in front of the camera. Make sure all text is clearly visible, then tap the capture button.",
  processing: "Please wait while we verify your Blue Badge. This usually takes just a few seconds.",
  confirmation: "Congratulations! Your registration is now complete. Your vehicle has been added to our whitelist. You may now use designated parking spaces. Thank you for using ParkWise.",
  lookup: "Enter your vehicle registration number to check your current parking status.",
  status_active: "Good news! Your vehicle registration is active. You are authorised to use designated parking spaces.",
  status_expired: "Your registration has expired. Please tap Register Now to renew your parking entitlement.",
  status_not_found: "We couldn't find a registration for this vehicle. Would you like to register now?"
};

// Category-specific success messages for registration confirmation
export const getCategorySuccessMessage = (category) => {
  const messages = {
    disabled_blue_badge: "Your Blue Badge has been successfully registered. Your vehicle has been added to our whitelist. You may now use designated parking spaces. Thank you for using ParkWise.",
    nhs_staff: "Your NHS Staff parking registration has been successfully registered. Your vehicle has been added to our whitelist. You may now use designated parking spaces. Thank you for using ParkWise.",
    adult_with_child: "Your Adult with Child parking registration has been successfully registered. Your vehicle has been added to our whitelist. You may now use designated parking spaces. Thank you for using ParkWise.",
    extra_wide: "Your Extra Wide parking registration has been successfully registered. Your vehicle has been added to our whitelist. You may now use designated parking spaces. Thank you for using ParkWise.",
    military: "Your Military parking registration has been successfully registered. Your vehicle has been added to our whitelist. You may now use designated parking spaces. Thank you for using ParkWise."
  };
  
  return messages[category] || KIOSK_MESSAGES.confirmation;
};

// Category-specific processing messages for registration processing screen
export const getCategoryProcessingMessage = (category) => {
  const messages = {
    disabled_blue_badge: "Please wait while we verify your Blue Badge. This usually takes just a few seconds.",
    nhs_staff: "Please wait while we process your NHS Staff parking registration. This usually takes just a few seconds.",
    adult_with_child: "Please wait while we process your Adult with Child parking registration. This usually takes just a few seconds.",
    extra_wide: "Please wait while we process your Extra Wide parking registration. This usually takes just a few seconds.",
    military: "Please wait while we process your Military parking registration. This usually takes just a few seconds."
  };
  
  return messages[category] || KIOSK_MESSAGES.processing;
};

// Pending/Manual Review message
export const PENDING_REVIEW_MESSAGE = "Your registration has been submitted for manual review. Our team will verify your details and you will receive an email confirmation once approved. Thank you for your patience.";

// Error/Failure message
export const REGISTRATION_FAILED_MESSAGE = "Sorry, your registration could not be completed. Please try again or speak to a member of staff for assistance.";

export default AiAvatar;
